import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import moment from "moment";
import ReleaseAnnouncementBanner, { RELEASE_ANNOUNCEMENT_KEY } from "./ReleaseAnnouncementBanner";
import PriorityBanner, {
	getPriorityDismissKey,
	getPriorityBannerSignature,
} from "./PriorityBanner";
import {
	fetchLatestScheduledMaintenance,
	fetchLatestPriorityBanner,
} from "../../api/firestoreService";

// Cache + polling windows (kept identical to the reference implementation).
const MAINTENANCE_CACHE_MS = 60 * 60 * 1000; // 1 hour
const PRIORITY_CACHE_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Short deterministic hash of the Firestore endpoint URLs. It is appended to the
 * cache keys so that whenever an endpoint changes (e.g. a new Firestore
 * collection in `.env.*`) every previously stored cache is orphaned and ignored,
 * and the banners re-fetch from the new source on the next load.
 */
const hashString = (str) => {
	let hash = 0;
	for (let i = 0; i < str.length; i += 1) {
		hash = (hash << 5) - hash + str.charCodeAt(i);
		hash |= 0; // force 32-bit int
	}
	return Math.abs(hash).toString(36);
};

const MAINTENANCE_SRC = process.env.REACT_APP_FIREBASE_PROJECT_FETCH_DATA_LINK || "";
const PRIORITY_SRC = process.env.REACT_APP_FIREBASE_PROJECT_PRIORITY_BANNER_DATA_LINK || "";

const MAINTENANCE_CACHE_KEY = `status_maintenance_cache_${hashString(MAINTENANCE_SRC)}`;
const PRIORITY_CACHE_KEY = `status_priority_cache_${hashString(PRIORITY_SRC)}`;

// One-time sweep: drop caches written by any earlier endpoint / key scheme.
try {
	Object.keys(localStorage)
		.filter(
			(k) =>
				(k.startsWith("status_maintenance_cache") && k !== MAINTENANCE_CACHE_KEY) ||
				(k.startsWith("status_priority_cache") && k !== PRIORITY_CACHE_KEY)
		)
		.forEach((k) => localStorage.removeItem(k));
} catch (e) {
	/* ignore storage errors */
}

const readCache = (key, maxAgeMs) => {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) return null;
		const cache = JSON.parse(raw);
		if (cache && cache.timestamp && Date.now() - cache.timestamp < maxAgeMs) {
			return cache.data;
		}
	} catch (e) {
		/* ignore malformed cache */
	}
	return null;
};

const writeCache = (key, data) => {
	try {
		localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
	} catch (e) {
		/* ignore storage errors */
	}
};

const readStorage = (key) => {
	try {
		return localStorage.getItem(key);
	} catch (e) {
		return null;
	}
};

/**
 * Dynamic Firestore banner system.
 *
 * Renders up to two stacked notification bars at the very top of the app:
 *   1. Scheduled maintenance  - re-validated every 1 hour  (1 hour cache)
 *   2. Priority incident/update - re-validated every 5 minutes (5 minute cache)
 *
 * The component is purely additive: it is `position: fixed` and publishes its
 * measured height to the `--status-banner-height` CSS variable, which the layout
 * uses to offset the topbar / content / sidebar. When nothing is visible the
 * variable is `0px` and the layout is untouched.
 */
function StatusBanners() {
	const [maintenanceData, setMaintenanceData] = useState(null);
	const [isMaintenanceVisible, setIsMaintenanceVisible] = useState(false);

	const [priorityData, setPriorityData] = useState(null);
	const [isPriorityVisible, setIsPriorityVisible] = useState(false);

	const wrapperRef = useRef(null);

	// 1. Scheduled maintenance (1 hour cache / poll)
	const checkMaintenanceStatus = useCallback(async () => {
		let data = readCache(MAINTENANCE_CACHE_KEY, MAINTENANCE_CACHE_MS);
		if (!data) {
			data = await fetchLatestScheduledMaintenance();
			writeCache(MAINTENANCE_CACHE_KEY, data);
		}

		if (data && data.startDate) {
			const datePart = String(data.startDate).split("T")[0];
			const todayStr = moment().format("YYYY-MM-DD");
			const tomorrowStr = moment().add(1, "day").format("YYYY-MM-DD");

			const isTodayOrTomorrow = datePart === todayStr || datePart === tomorrowStr;
			const isDismissed = readStorage(RELEASE_ANNOUNCEMENT_KEY) === data.id;
			const isCompleted = (data.status || "").toLowerCase() === "completed";

			if (isTodayOrTomorrow && !isDismissed && !isCompleted) {
				setMaintenanceData(data);
				setIsMaintenanceVisible(true);
				return;
			}
		}
		setIsMaintenanceVisible(false);
	}, []);

	// 2. Priority incident / update (5 minute cache / poll)
	const checkPriorityBannerStatus = useCallback(async () => {
		let data = readCache(PRIORITY_CACHE_KEY, PRIORITY_CACHE_MS);
		if (!data) {
			data = await fetchLatestPriorityBanner();
			writeCache(PRIORITY_CACHE_KEY, data);
		}

		if (data && data.isActive) {
			// Dismissal is tracked under a per-company key; the signature embeds
			// updatedAt so an admin edit re-shows the banner for everyone.
			const signature = getPriorityBannerSignature(data);
			const isDismissed = readStorage(getPriorityDismissKey()) === signature;
			if (!isDismissed) {
				setPriorityData(data);
				setIsPriorityVisible(true);
				return;
			}
		}
		setIsPriorityVisible(false);
	}, []);

	// 3. Initial fetch + polling intervals, cleaned up on unmount.
	useEffect(() => {
		checkMaintenanceStatus();
		checkPriorityBannerStatus();

		const maintenanceIntervalId = setInterval(checkMaintenanceStatus, MAINTENANCE_CACHE_MS);
		const priorityIntervalId = setInterval(checkPriorityBannerStatus, PRIORITY_CACHE_MS);

		return () => {
			clearInterval(maintenanceIntervalId);
			clearInterval(priorityIntervalId);
		};
	}, [checkMaintenanceStatus, checkPriorityBannerStatus]);

	// 4. Publish the rendered height so the layout can offset itself.
	useLayoutEffect(() => {
		const root = document.documentElement;
		const height =
			isMaintenanceVisible || isPriorityVisible
				? (wrapperRef.current && wrapperRef.current.offsetHeight) || 0
				: 0;
		root.style.setProperty("--status-banner-height", `${height}px`);

		return () => {
			root.style.setProperty("--status-banner-height", "0px");
		};
	}, [isMaintenanceVisible, isPriorityVisible, maintenanceData, priorityData]);

	if (!isMaintenanceVisible && !isPriorityVisible) return null;

	return (
		<div
			ref={wrapperRef}
			style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1500 }}
		>
			{isMaintenanceVisible && (
				<ReleaseAnnouncementBanner
					maintenanceData={maintenanceData}
					onDismiss={() => setIsMaintenanceVisible(false)}
				/>
			)}
			{isPriorityVisible && (
				<PriorityBanner
					bannerData={priorityData}
					onDismiss={() => setIsPriorityVisible(false)}
				/>
			)}
		</div>
	);
}

export default StatusBanners;
